import uaeSubnets from '../data/uae_isp_subnets.json';

/**
 * Convert IP address to integer for comparison
 * @param {string} ip - IP address in dotted notation (e.g., "192.168.1.1")
 * @returns {number} Integer representation of IP
 */
function ipToInt(ip) {
  const parts = ip.split('.');
  return (parseInt(parts[0]) << 24) +
         (parseInt(parts[1]) << 16) +
         (parseInt(parts[2]) << 8) +
         parseInt(parts[3]);
}

/**
 * Parse CIDR notation to get range
 * @param {string} cidr - CIDR notation (e.g., "192.168.1.0/24")
 * @returns {object} Object with start and end IP integers
 */
function cidrToRange(cidr) {
  const [baseIp, bits] = cidr.split('/');
  const mask = -1 << (32 - parseInt(bits));
  const baseInt = ipToInt(baseIp);
  const start = (baseInt & mask) >>> 0; // Unsigned 32-bit integer
  const end = (start + (~mask & 0xFFFFFFFF)) >>> 0; // Unsigned 32-bit integer

  return { start, end };
}

/**
 * Check if an IP address is within UAE ISP ranges
 * @param {string} ip - IP address to check
 * @returns {boolean} True if IP is from UAE
 */
export function isUAEIP(ip) {
  if (!ip || typeof ip !== 'string') {
    return false;
  }

  // Handle localhost and private IPs - assume UAE for development
  if (ip === '127.0.0.1' || ip === 'localhost' || ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('172.16.')) {
    return true;
  }

  try {
    const ipInt = ipToInt(ip);

    // Check all ISP subnets
    for (const ispKey in uaeSubnets.isps) {
      const isp = uaeSubnets.isps[ispKey];

      for (const subnet of isp.subnets) {
        const range = cidrToRange(subnet.cidr);

        if (ipInt >= range.start && ipInt <= range.end) {
          return true;
        }
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking UAE IP:', error);
    // Default to UAE if detection fails
    return true;
  }
}

/**
 * Get detected currency based on IP location
 * @param {string} ip - IP address
 * @returns {string} Currency code ('AED' or 'USD')
 */
export function getDetectedCurrency(ip) {
  return isUAEIP(ip) ? 'AED' : 'USD';
}

/**
 * Detect user's IP from metadata
 * This should be called with the IP from the backend geolocation service
 * @param {object} metadata - Metadata object containing IP info
 * @returns {object} Object with isUAE and currency
 */
export function detectLocationFromMetadata(metadata) {
  const ip = metadata?.ip || null;
  const isUAE = ip ? isUAEIP(ip) : true; // Default to UAE if no IP
  const currency = getDetectedCurrency(ip);

  return {
    isUAE,
    currency,
    ip,
    detectedFrom: ip ? 'IP Detection' : 'Default (UAE)'
  };
}
